import React, { useState, useMemo } from 'react';
import CalcShell from '../CalcShell';

// Distance units → miles
const DIST = { mi:1, km:0.621371, m:0.000621371 };
// Fuel efficiency conversions
function toMpg(v, u) {
  if (u === 'mpg') return v;
  if (u === 'km/L') return v * 2.35215;
  if (u === 'L/100km') return v ? 235.215 / v : 0;
  if (u === 'L/mi') return v ? 1 / (v / 3.78541) : 0;
  return v;
}
// Price → per gallon
function toPerGal(v, u) {
  if (u === '$/gal') return v;
  if (u === '$/L') return v * 3.78541;
  if (u === '$/mi') return null; // direct cost per mile
  return v;
}

export default function FuelCostCalc() {
  const [distance, setDistance] = useState(500);
  const [distUnit, setDistUnit] = useState('mi');
  const [eff, setEff] = useState(28);
  const [effUnit, setEffUnit] = useState('mpg');
  const [price, setPrice] = useState(3.75);
  const [priceUnit, setPriceUnit] = useState('$/gal');
  const [roundTrip, setRoundTrip] = useState(false);

  const c = useMemo(() => {
    let miles = Number(distance) / DIST[distUnit];
    if (roundTrip) miles *= 2;
    const mpg = toMpg(Number(eff), effUnit);
    if (!mpg) return null;
    const gallons = miles / mpg;
    const perGal = toPerGal(Number(price), priceUnit);
    const cost = gallons * perGal;
    return { miles, gallons, cost, cpm: miles ? cost/miles : 0 };
  }, [distance, distUnit, eff, effUnit, price, priceUnit, roundTrip]);

  return (
    <CalcShell look="cnet" title="Fuel Cost Calculator" subtitle="Estimate the fuel cost for any trip using multiple distance, efficiency and price units.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Trip Distance</label>
            <div className="flex gap-2"><input type="number" value={distance} onChange={e => setDistance(e.target.value)} className="flex-1"/>
              <select value={distUnit} onChange={e => setDistUnit(e.target.value)}><option>mi</option><option>km</option><option>m</option></select>
            </div>
          </div>
          <label className="flex items-center gap-2 mb-3"><input type="checkbox" checked={roundTrip} onChange={e => setRoundTrip(e.target.checked)}/> Round trip</label>
          <div className="field"><label>Fuel Efficiency</label>
            <div className="flex gap-2"><input type="number" step="0.1" value={eff} onChange={e => setEff(e.target.value)} className="flex-1"/>
              <select value={effUnit} onChange={e => setEffUnit(e.target.value)}><option value="mpg">mpg</option><option value="km/L">km/L</option><option value="L/100km">L/100km</option><option value="L/mi">L/mi</option></select>
            </div>
          </div>
          <div className="field"><label>Fuel Price</label>
            <div className="flex gap-2"><input type="number" step="0.01" value={price} onChange={e => setPrice(e.target.value)} className="flex-1"/>
              <select value={priceUnit} onChange={e => setPriceUnit(e.target.value)}><option value="$/gal">$/gal</option><option value="$/L">$/L</option></select>
            </div>
          </div>
        </div>
        <div>
          {c && (
            <>
              <div className="result-panel mb-4"><div className="result-title">Trip Cost</div><div className="result-value">${c.cost.toFixed(2)}</div></div>
              <table className="data-table">
                <tbody>
                  <tr><td>Distance (miles)</td><td className="text-right">{c.miles.toFixed(1)}</td></tr>
                  <tr><td>Fuel Used (gallons)</td><td className="text-right">{c.gallons.toFixed(2)}</td></tr>
                  <tr><td>Cost per mile</td><td className="text-right">${c.cpm.toFixed(3)}</td></tr>
                </tbody>
              </table>
            </>
          )}
        </div>
      </div>
    </CalcShell>
  );
}
